import {
	Document,
	ThumbnailStatus,
	BLAdmin,
} from '../types/working-model';
import { v4 as uuid } from 'uuid';
import { Post, Put } from './helpers';
import { extractFileType } from '../utils/utils';
import { postDocument } from './content-connection';
import {
	FILE_NUMBER_MAP,
	FILE_TYPES,
	THUMBNAIL_TYPES,
} from '../store/utils/content-utils';

export const GetUploadEndpoint = async (token: string, prefix: string, filename: string, isThumb = false, isLiveSession = false): Promise<any> => {
	let url = '/v3/admin/channel/upload-endpoint';
	if (isLiveSession) {
		url = '/v3/admin/live-session/upload-endpoint';
	}

	return Put(url, token, { filename, prefix, isThumb }, false, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}

			throw new Error(url);
		});
};

export const UploadToS3 = async (
	url: string,
	file: File | Blob,
	data: { [key: string]: any },
	progress?: (uploadedBytes: number) => void,
	filename?: string,
	returnFileHttpRequest?: (request: XMLHttpRequest) => any
): Promise<void> => {
	const formData = new FormData();
	for (const key in data) {
		formData.append(key, data[key]);
	}
	formData.append('file', file, filename);

	return new Promise((resolve) => {
		const request = new XMLHttpRequest();
		returnFileHttpRequest?.(request);
		request.open('POST', url);

		request.upload.onprogress = (e: ProgressEvent<EventTarget>) => {
			const uploaded = e.loaded;
			progress?.(uploaded);
		};

		request.onreadystatechange = () => {
			if (request.readyState === 4) {
				resolve();
			}
		};

		request.send(formData);
	});
};

export const MakeItemPublic = async (token: string, filename: string, isLiveSession = false): Promise<any> => {
	let url = '/v3/admin/channel/upload-endpoint/make-public';
	if (isLiveSession) {
		url = '/v3/admin/live-session/upload-endpoint/make-public';
	}

	return Put(url, token, { filename }, false, true)
		.then(res => {
			if (res.ok) {
				return res.ok;
			}

			throw new Error(`${url} didn't work`);
		});
};

export function UploadFile(
	user: BLAdmin,
	token: string,
	file: File | Blob,
	onProgress?: (uploadedBytes: number) => void
): Promise<string> {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve, reject) => {
		try {
			if (file instanceof File) {
				const filenameParts = file.name.split(".");
				const suffix = filenameParts[filenameParts.length - 1];

				//if this is an image, we want to pull the dimensions out of the image 
				//and put them into the filename
				if (['image/jpeg', 'image/gif', 'image/png'].includes(file.type)) {
					//read the file out as binary
					const reader = new FileReader();
					reader.onload = () => {
						//when image is finished being read
						if (typeof reader.result === 'string') {

							//create an image element and load in the image in base64
							const image = document.createElement('img');
							image.onload = async () => {
								//image's height and width will be available to us
								//write that into the filename
								const filename = `${Date.now()}_w${image.width}_h${image.height}.${suffix}`;

								const endpoint = await GetUploadEndpoint(
									token,
									`${user.active_channel}/${uuid()}`,
									filename
								);
								await UploadToS3(endpoint.url, file, endpoint.fields, onProgress);
								await MakeItemPublic(token, endpoint.fields.Key);
								resolve(endpoint.url + '/' + endpoint.fields.Key);
							};
							image.src = reader.result;
						}
					};

					//reads the file
					reader.readAsDataURL(file);
				} else {
					const filename = file instanceof File ? file.name.replace(/[^0-9a-zA-Z.]/g, '-') : `${Date.now()}.jpg`;
					const endpoint = await GetUploadEndpoint(
						token,
						`${user.active_channel}/${uuid()}`,
						filename
					);
					await UploadToS3(endpoint.url, file, endpoint.fields, onProgress);
					await MakeItemPublic(token, endpoint.fields.Key);
					resolve(endpoint.url + '/' + endpoint.fields.Key);
				}
			} else {
				// not a blob, just go ahead and resolve empty string
				resolve('');
			}
		} catch (e) {
			console.error(e);
			reject();
		}
	});
}

export function UploadAndConvertDocument(
	user: BLAdmin,
	token: string,
	file: File,
	onProgress?: (uploadedBytes: number) => void
): Promise<Document> {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve, reject) => {
		try {
			const prefix = `/uploads/${user.active_channel
				}/documents/${uuid()}`;
			const fileToUpload = new File(
				[file],
				file.name
					.replace(/[`~!@#$%^&*|+=?;:'",<>{}[\]\\/]/gi, '')
					.replace(/\s/g, '_')
			);
			const fileName = fileToUpload.name;

			const endpoint = await GetUploadEndpoint(token, prefix, fileName);
			await UploadToS3(endpoint.url, file, endpoint.fields, onProgress);
			await MakeItemPublic(token, endpoint.fields.Key);

			const fileExt = extractFileType(file.name);
			const location = endpoint.url + '/' + endpoint.fields.Key;

			const isImage = FILE_NUMBER_MAP[fileExt] === FILE_TYPES.IMAGES;
			const isThumbnailType = THUMBNAIL_TYPES.has(fileExt);
			console.log(
				fileExt,
				'can be converted to thumbnail: ',
				isThumbnailType
			);

			const document = await postDocument(
				{
					original_name: file.name,
					location: location,
					thumbnail: isImage ? location : null,
					thumbnail_status: isThumbnailType
						? ThumbnailStatus.generating
						: ThumbnailStatus.never,
					filesize: file.size,
					display_name: { base: file.name, changed: 'true' },
				},
				token
			);

			if (isThumbnailType) {
				const body = {
					document: document.document,
					ext: fileExt,
					prefix: prefix,
					filename: fileName,
				};
				await Post(
					'/v3/admin/channel/documents/convert',
					token,
					body,
					true
				);
			}
			resolve(document);
		} catch (e) {
			console.error(e);
			reject();
		}
	});
}
